<template>
  <v-layout>
    <v-flex>
      <v-row align="center" no-gutters>
        <v-col class="text-left ml-n6" cols="12" md="6">
          <v-breadcrumbs :items="subNavs"></v-breadcrumbs>
        </v-col>
        <v-col class="text-right"  cols="12" md="6">
          <ActionButtons
            @doClose="doClose"
            @doPrint="doPrint"
            :actionBtnSettings="actionBtnSettings"
          >
          </ActionButtons>
        </v-col>
      </v-row>

      <v-card class="pa-6 mt-3">
        <v-card-title>
          Dispatch Order
          <v-spacer></v-spacer>
          <v-btn
            v-if="delivery.status == $getConstDeliveryStatus('DELIVERY_STATUS_CREATED')"
            color="primary mr-6"
            depressed
            rounded
            @click="delivered(delivery)">
            <span v-if="loading" class="mx-4">Please wait...</span>
            <span v-if="!loading" class="mx-4">Order Complete</span>
          </v-btn>
          <DeliveryStatus :status="delivery.status" :statuses="statuses"></DeliveryStatus>
        </v-card-title>
        <v-card-subtitle>
          #{{delivery.delivery_no}}
          <v-spacer></v-spacer>
        </v-card-subtitle>
        <v-card-text class="text--primary mt-6">

          <v-row no-gutters>
            <v-col cols="12" sm="12" md="6">
              <v-list two-line>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>
                      mdi-account-outline
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{delivery.deliver_to_name}}</v-list-item-title>
                    <v-list-item-subtitle>Dispatch To</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="12" sm="12" md="6">
              <v-list two-line>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>
                      mdi-calendar
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{ delivery.delivery_date | moment("MMM D, YYYY") }}</v-list-item-title>
                    <v-list-item-subtitle>Dispatch Date</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>

            <v-col cols="12" sm="12" md="6">
              <v-list two-line>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>
                      mdi-map-marker-outline
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{delivery.deliver_to_address}}</v-list-item-title>
                    <v-list-item-subtitle>Address</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>

            <v-col cols="12" sm="12" md="6">
              <v-list two-line>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>
                      mdi-receipt-text-outline
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{delivery.ref_no ? delivery.ref_no : '-'}}</v-list-item-title>
                    <v-list-item-subtitle>Reference No.</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="12" sm="12" md="6">
              <v-list two-line>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>
                      mdi-phone-classic
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{delivery.deliver_to_contact_no}}</v-list-item-title>
                    <v-list-item-subtitle>Contact No.</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="12" sm="12" md="6">
              <v-list two-line>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>
                      mdi-note-outline
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{delivery.remarks ? delivery.remarks : '-'}}</v-list-item-title>
                    <v-list-item-subtitle>Remarks</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
            <v-col cols="12" sm="12" md="6">
              <v-list two-line>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon>
                      mdi-truck-delivery-outline
                    </v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>{{delivery.plate_no}}</v-list-item-title>
                    <v-list-item-subtitle>Plate No.</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-col>
          </v-row>

          <v-data-table
            class="mt-10"
            :headers="headers"
            :items="items"
            :loading="loading"
            :items-per-page="1000"
            hide-default-footer
            loading-text="Loading Items..."
            no-data-text="No Items"
            ref="table">

            <template v-slot:[`item.shipping_mark`]="{ item }">
              <v-tooltip bottom v-if="item.has_storage_fee">
                <template v-slot:activator="{ on }">
                  <v-icon
                    v-on="on"
                    color="error"
                    class="mr-2">
                    mdi-alert-circle
                  </v-icon>
                </template>
                <span>Need to settle storage fee</span>
              </v-tooltip>

              <v-icon
                size="24"
                class="mr-2">
                {{ isShipmentForSea(item.shipment_type) ? 'mdi-ferry' : 'mdi-airplane' }}
              </v-icon>
              {{ item.shipping_mark }}
            </template>

            <template v-slot:[`item.qty`]="{ item }">
              {{ formatNumber(item.qty) }}
            </template>
            <template v-slot:[`item.cbm`]="{ item }">
              {{ formatNumber(item.cbm) }}
            </template>
            <template v-slot:[`item.amount`]="{ item }">
              {{ formatNumberAmount($numeral, item.amount) }}
            </template>


          </v-data-table>
          <v-divider></v-divider>
        </v-card-text>
        <v-card-title class="mt-12">
          Dispatch Confirmation Images
          <v-chip
            text-color="white"
            class="ml-6"
            @click="clickFile()"
            color="primary">
            <v-icon small left>
              mdi-image-plus
            </v-icon>
            <strong>Upload Image</strong>
          </v-chip>
          <input v-show="false" type="file" id="file" ref="file" multiple v-on:change="onChangeFileUpload()" accept="image/png, image/jpeg, image/jpg"/>
        </v-card-title>
        <v-card-text>
          <AlertMessage :alerMessage="alerMessage"></AlertMessage>

          <v-row class="mt-6">
            <v-col
              v-for="img in dorImages"
              :key="img.id"
              class="d-flex child-flex"
              cols="4"
            >
              <v-img
                :src="img.loc"
                :lazy-src="img.loc"
                aspect-ratio="1"
                class="grey lighten-2"
              >
                <div class="float-right mr-2 mt-2 mb-2">
                  <v-btn
                    class="mr-2"
                    color="grey"
                    fab
                    small
                    dark
                    depressed
                    @click="showImg(img.loc)"
                  >
                    <v-icon>mdi-magnify</v-icon>
                  </v-btn>

                  <v-btn
                    color="red"
                    fab
                    small
                    dark
                    depressed
                    @click="confirmDeleteImg(img)"
                  >
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </div>
                <template v-slot:placeholder>
                  <v-row
                    class="fill-height ma-0"
                    align="center"
                    justify="center"
                  >
                    <v-progress-circular
                      indeterminate
                      color="grey lighten-5"
                    ></v-progress-circular>
                  </v-row>
                </template>
              </v-img>
            </v-col>
          </v-row>
        </v-card-text>
      </v-card>

      <SnackBar :snackbarDetails="snackbarDetails"></SnackBar>
      <ConfirmationDialog
        :dialog="dialog"
        @doConfirm="doConfirm"
        @doCancel="doCancel"
      >
      </ConfirmationDialog>
      <Loader :loading="loading"></Loader>
    </v-flex>
  </v-layout>
</template>

<script>
  import SnackBar from '@/components/snackbar/SnackBar.vue'
  import Loader from '@/components/Loader.vue'
  import { mapGetters } from "vuex";
  import ActionButtons from '@/components/ActionButtons.vue'
  import AlertMessage from '@/components/AlertMessage.vue'
  import DeliveryStatus from '@/components/DeliveryStatus.vue'
  import ConfirmationDialog from '@/components/dialog/ConfirmationDialog.vue'

  export default {
    name: 'DOView',
    data: () => ({
      id: null,
      file: '',
      imgUploading: false,
      operationsNamespace: 'operations',
      loading: false,
      isLoading: false,
      deletedItems: [],
      perPage: 2000,
      print: false,
      actionBtnSettings: {
        edit: 0,
        save: 0,
        refresh: 0,
        close: 1,
        print: 1,
        send: 0,
        save_and_print: 0,
        save_disabled: false,
        close_disabled: false,
        print_disabled: false,
        save_and_print_disabled: false
      },
      snackbarDetails: {
        color: 'sb-success',
        msg: '',
        show: false
      },
      alerMessage:{
        show: false,
        status: '',
        type: 'error',
        omitMessage: '',
        messages: []
      },
      dialog: {
        show: false,
        title: 'Delete Item',
        body: 'Are you sure you want to delete this item?',
        type: '',
        item: {},
        confirmBtn: 'Yes',
        cancelBtn: 'Cancel'
      },
      dorImages: [],
      delivery: {
        id: '',
        delivery_no: '',
        ref_no: '',
        deliver_to_name: '',
        deliver_to_contact_no: '',
        deliver_to_address: '',
        delivery_date: '',
        remarks: '',
        sub_total: 0,
        discounts: 0,
        other_charges: 0,
        net_amount: 0
      },
      statuses: [],
      subNavs: [
        {
          text: 'Dispatch Orders',
          disabled: false,
          exact: true,
          to: {
            name: 'delivery-orders'
          },
        },
        {
          text: 'View Dispatch Order',
          disabled: true,
          href: 'breadcrumbs_link_2',
        },
      ],
      items: [],
      headers: [
        {
          text: 'Shipping Mark',
          align: 'start',
          value: 'shipping_mark',
          sortable: false,
          width: 250
        },
        {
          text: 'Container',
          align: 'start',
          value: 'container',
          sortable: false
        },
        {
          text: 'Item',
          align: 'start',
          value: 'description',
          sortable: false
        },
        {
          text: 'Qty',
          align: 'center',
          value: 'qty',
          sortable: false,
          width: 180
        }
      ]
    }),
    watch: {
    },
    computed: {
      ...mapGetters(['formatNumberAmount', 'formatNumber', 'formatNumberAmountNoComma', 'getCurrentUser', 'forSea']),
    },
    methods: {
      deleteImg () {
        self.loading = true

        this.$http.secured.post(this.operationsNamespace+'/deliveries/confirmation-images/delete/'+this.dialog.item.id, '')
          .then(response => {
            this.getDor()
            this.showMessage("Image has been successfully deleted.", 'success')
          })
          .catch(error => {
            if (error.response) {
              this.showMessage(error.response.data.messages, 'error')
            }else{
              this.showMessage("Something went wrong! Please try again.", 'error')
            }
          })
          .then(function () {
            self.loading = false
          })
      },
      confirmDeleteImg (img) {
        this.dialog = {
          show: true,
          title: 'Delete Image',
          body: 'Are you sure you want to delete this image?',
          type: 'confirm-remove-img',
          item: img,
          confirmBtn: 'Yes',
          cancelBtn: 'No'
        }
      },
      showImg (imgLoc) {
        window.open(imgLoc, '_newtab');
      },
      clickFile () {
        let file = document.getElementById('file');
        file.click()
      },
      getDor () {
        let self = this
        this.$http.plain.get(this.operationsNamespace+'/deliveries/confirmation-images/'+this.id)
          .then(response => {
            this.dorImages = response.data
          })
          .catch(error => {
            console.log(error);
          })
      },
      onChangeFileUpload (){
        this.imgUploading = true
        this.alerMessage.show = false

        let self = this
        let formData = new FormData();

        for (let i = 0; i < this.$refs.file.files.length; i++) {
          formData.append('files[]', this.$refs.file.files[i]);
        }

        this.$http.secured.post(this.operationsNamespace+'/deliveries/confirmation-images/'+this.id+'/upload',
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          })
          .then(response => {
            console.log(response.data);
            this.getDor()
          })
          .catch(error => {
            if (error.response) {
              this.alerMessage.messages = error.response.data.messages
              this.alerMessage.show = true
            }else{
              this.showMessage("Something went wrong! Please try again.", 'error')
            }
          })
          .then(function () {
            self.imgUploading = false
          })
      },
      isShipmentForSea (shipment_type){
        return this.forSea(shipment_type, this)
      },
      doClose (){
        this.$router.push({ name: 'delivery-orders' })
      },
      delivered (delivery){
        this.dialog = {
          show: true,
          title: 'Order Complete',
          body: 'Are you sure the dispatch order '+delivery.delivery_no+' has been completed?',
          type: 'confirm-delivered',
          item: delivery,
          confirmBtn: 'Yes',
          cancelBtn: 'No'
        }
      },
      deliveryMade (){
        self.loading = true

        this.$http.secured.post(this.operationsNamespace+'/deliveries/delivered/'+this.id, '')
          .then(response => {
            this.delivery.status = this.$getConstDeliveryStatus('DELIVERY_STATUS_DELIVERED')
            this.showMessage("Dispatch order has been successfully completed.", 'success')
          })
          .catch(error => {
            if (error.response) {
              this.showMessage(error.response.data.messages, 'error')
            }else{
              this.showMessage("Something went wrong! Please try again.", 'error')
            }
          })
          .then(function () {
            self.loading = false
          })
      },
      doConfirm () {
        this.dialog.show = false
        if(this.dialog.type == 'confirm-delivered'){
          this.deliveryMade()
        }else if(this.dialog.type == 'confirm-remove-img'){
          this.deleteImg()
        }
      },
      doCancel () {
        this.dialog.show = false
      },
      doPrint (){
        this.print = true
        this.getDelivery()
      },
      getDelivery () {
        if(this.print){
          const currentDate = new Date();
          const unixTimestamp = Math.floor(currentDate.getTime() / 1000)

          window.open('/api/'+this.operationsNamespace+'/deliveries/'+this.id+'?print=true&v='+unixTimestamp, '_newtab');
          return false
        }
        let self = this
        this.loading = true
        this.$http.plain.get(this.operationsNamespace+'/deliveries/'+this.id)
          .then(response => {
            this.delivery = response.data
            this.items = this.delivery.items
          })
          .catch(error => {
            console.log(error);
          })
          .then(function () {
            self.loading = false
          })
      },
      getDefaultData () {
        let self = this
        this.$http.plain.get('/default-data?delivery_statuses=true')
          .then(response => {
            this.statuses = response.data.delivery_statuses
          })
          .catch(error => {
            console.log(error);
          })
      },
      showMessage(msg, color){
        this.snackbarDetails = {
          color: color,
          msg: msg,
          show: true
        }
      }
    },
    mounted() {
      if(this.$route.params.id != 'add'){
        this.id = this.$route.params.id
      }
      this.getDefaultData()
      this.getDelivery()
      this.getDor()
    },
    components: {
      SnackBar,
      Loader,
      ActionButtons,
      DeliveryStatus,
      ConfirmationDialog,
      AlertMessage
    }
  }
</script>
